import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { blockedResponse, noteRateLimited, rateLimitKey } from './utils/rateLimit';

const NAME_KEYS = new Set([
  "firstName",
  "lastName",
  "fullName",
  "displayName",
  "visitorName",
  "occupantName",
  "reporterName",
  "contactPersonName",
  "name",
  "buildingName",
  "phaseName",
  "corporateName",
  "companyName",
  "tenantName",
  "dependentName",
  "subjectName",
  "requesterName",
  "checkedInByName",
  "checkedOutByName",
  "ownerFullName",
  "ownerName"
]);

function toTitleCase(str) {
  if (!str || typeof str !== "string") return str;
  // Skip if it looks like an email, URL, phone number, hex-color or code
  if (
    str.includes("@") ||
    str.startsWith("http://") ||
    str.startsWith("https://") ||
    str.startsWith("#") ||
    /^\+?[0-9\s-]{7,}$/.test(str)
  ) {
    return str;
  }
  return str
    .toLowerCase()
    .split(/\s+/)
    .map(word => {
      if (!word) return "";
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
}

function formatNamesInJson(obj) {
  if (!obj || typeof obj !== "object") return obj;

  if (Array.isArray(obj)) {
    return obj.map(item => formatNamesInJson(item));
  }

  const result = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const val = obj[key];
      if (typeof val === "string" && NAME_KEYS.has(key)) {
        result[key] = toTitleCase(val);
      } else if (val && typeof val === "object") {
        result[key] = formatNamesInJson(val);
      } else {
        result[key] = val;
      }
    }
  }
  return result;
}

const originalFetch = window.fetch;

// Identical GETs already in flight (same URL and token) share one network request; each
// caller gets its own copy of the response. This covers React StrictMode's double effects
// in development and widgets that ask for the same data at the same time.
const inFlightGets = new Map();

function getHeader(headers, name) {
  if (!headers) return "";
  if (typeof headers.get === "function") return headers.get(name) || "";
  const key = Object.keys(headers).find((k) => k.toLowerCase() === name.toLowerCase());
  return key ? headers[key] : "";
}

function withoutContentType(headers) {
  if (typeof headers.get === "function") {
    const copy = new Headers(headers);
    copy.delete("Content-Type");
    return copy;
  }
  return Object.fromEntries(Object.entries(headers).filter(([k]) => k.toLowerCase() !== "content-type"));
}

// Resolves with the shared response, or rejects like fetch when this caller's signal aborts.
function withCallerAbort(promise, signal) {
  if (!signal) return promise;
  if (signal.aborted) return Promise.reject(new DOMException("Aborted", "AbortError"));
  return new Promise((resolve, reject) => {
    const onAbort = () => reject(new DOMException("Aborted", "AbortError"));
    signal.addEventListener("abort", onAbort, { once: true });
    promise.then(resolve, reject).finally(() => signal.removeEventListener("abort", onAbort));
  });
}

window.fetch = async function (url, options) {
  // Don't resend an action the API has rate limited until its Retry-After has passed.
  const limitKey = rateLimitKey(url, options);
  const blocked = blockedResponse(limitKey);
  if (blocked) return blocked;

  const method = String(options?.method || (typeof url === "object" && url?.method) || "GET").toUpperCase();
  // The API rejects a GET that declares a JSON body but sends none (400 serializer error),
  // so bodiless GET/HEAD requests never carry a Content-Type.
  if ((method === "GET" || method === "HEAD") && !options?.body && options?.headers) {
    options = { ...options, headers: withoutContentType(options.headers) };
  }
  // When a token expires, every request running at that moment gets a 401 and asks to
  // refresh with the same refresh token. Sending them all would let the first one rotate
  // the token and the rest fail (signing the user out), so they share one refresh call.
  const isRefresh = typeof url === "string" && /\/auth\/refresh$/.test(url.split("?")[0]);
  if ((method === "GET" || isRefresh) && typeof url === "string") {
    const key = `${method} ${url}|${getHeader(options?.headers, "Authorization")}|${isRefresh ? options?.body : ""}`;
    let shared = inFlightGets.get(key);
    if (!shared) {
      // Sent without the caller's signal so one caller cancelling doesn't fail the others.
      const { signal, ...rest } = options || {};
      shared = fetchAndFormat(url, rest, limitKey).finally(() => inFlightGets.delete(key));
      inFlightGets.set(key, shared);
    }
    const response = await withCallerAbort(shared, options?.signal);
    return response.clone();
  }

  return fetchAndFormat(url, options, limitKey);
};

async function fetchAndFormat(url, options, limitKey) {
  let modifiedOptions = options;
  if (options && options.body && typeof options.body === "string") {
    try {
      const parsed = JSON.parse(options.body);
      const formatted = formatNamesInJson(parsed);
      modifiedOptions = {
        ...options,
        body: JSON.stringify(formatted)
      };
    } catch (e) {
      // Not JSON or parse failed
    }
  }

  const response = await originalFetch(url, modifiedOptions);
  if (response.status === 429) await noteRateLimited(limitKey, response);
  const clone = response.clone();

  try {
    const contentType = clone.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
      const data = await clone.json();
      const formattedData = formatNamesInJson(data);

      return new Response(JSON.stringify(formattedData), {
        status: response.status,
        statusText: response.statusText,
        headers: response.headers
      });
    }
  } catch (err) {
    // Silent fallback
  }

  return response;
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <BrowserRouter>
      <App />
    </BrowserRouter>
  </React.StrictMode>
);

reportWebVitals();